/**
 * Channel Inbound API Client — Landlord Portal
 *
 * Test harness (non-production only): inject inbound messages as an actor and read their channels.
 */

import { apiFetch } from '../lib/api';

export interface ChannelInboundBody {
  actorType: 'tenant' | 'manager' | 'vendor';
  actorId: string;
  content: string;
  /** The repair case the harness is open on (a vendor's email reply is addressed to its alias). */
  caseId: string;
}

export interface ChannelInboundResult {
  /** True when the message woke an agent. */
  eventEmitted: boolean;
  channelId: string | null;
  /** Why nothing was delivered, when it wasn't. */
  note: string | null;
}

export async function postChannelInbound(body: ChannelInboundBody): Promise<ChannelInboundResult> {
  return apiFetch('/landlord/api/repair-harness/channel-inbound', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface HarnessChannelMessage {
  id: string;
  channelId: string;
  direction: 'inbound' | 'outbound';
  content: string;
  createdAt: string;
  routingDecision: 'pending' | 'ignored' | 'attached' | 'created';
  routedToCaseId: string | null;
  /** Delivery lifecycle — an email to a vendor can sit `pending`/`held_for_review` or end `failed`/`bounced`. */
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed' | 'bounced' | 'rejected' | 'held_for_review';
}

export interface ChannelMessagesResult {
  messages: HarnessChannelMessage[];
  channelId: string | null;
}

/** Channel messages for an actor; `caseId` hides the agent's replies about other cases (inbound is always shown). */
export async function getChannelMessages(actorId: string, caseId?: string): Promise<ChannelMessagesResult> {
  const params = new URLSearchParams({ actorId });
  if (caseId) params.set('caseId', caseId);
  return apiFetch(`/landlord/api/repair-harness/channel-messages?${params.toString()}`);
}
